/**
 * Team Detail Page
 * 
 * Full team view with members, activity, and team-specific settings.
 * Users can manage team members and access team functionality.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faUserPlus,
  faArrowLeft,
  faBox,
  faEdit,
  faTrash,
  faCrown,
  faUserShield,
  faCheck,
  faTimes,
  faCog,
  faHistory,
  faChartLine,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import { teamsAPI } from '../services/api';
import { useRBAC } from '../hooks/useRBAC';
import { 
  Permission,
} from '../types/rbac';
import '../styles/TeamDetail.css';

interface TeamMember {
  id: string;
  role: string;
  joinedAt: string;
  user: {
    id: string;
    email: string;
    name?: string;
  };
}

interface Team {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  workspaceId: string;
  workspace?: {
    id: string;
    name: string;
  };
  createdAt: string;
}

const TeamDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission, workspaceRole } = useRBAC();
  
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modals
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  
  // Add member form
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState('MEMBER');
  
  // Edit team form
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editColor, setEditColor] = useState('');
  
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const TEAM_COLORS = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
  ];

  const fetchTeam = useCallback(async () => {
    try {
      setLoading(true);
      const response = await teamsAPI.getById(id!);
      setTeam(response.data);
      setEditName(response.data.name);
      setEditDescription(response.data.description || '');
      setEditColor(response.data.color || TEAM_COLORS[0]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchMembers = useCallback(async () => {
    try {
      const response = await teamsAPI.getMembers(id!);
      setMembers(response.data);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchTeam();
      fetchMembers();
    }
  }, [id, fetchTeam, fetchMembers]);

  useEffect(() => {
    if (actionSuccess) {
      const timer = setTimeout(() => setActionSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccess]);

  const handleAddMember = async () => {
    if (!memberEmail.trim()) return;
    
    try {
      setActionLoading(true);
      await teamsAPI.addMember(id!, { email: memberEmail.trim(), role: memberRole });
      
      setShowAddMemberModal(false);
      setMemberEmail('');
      setMemberRole('MEMBER');
      setActionSuccess('Member added to team!');
      fetchMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add member');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this member from the team?')) return;
    
    try {
      await teamsAPI.removeMember(id!, userId);
      setActionSuccess('Member removed from team!');
      fetchMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleUpdateTeam = async () => {
    if (!editName.trim()) return;
    
    try {
      setActionLoading(true);
      await teamsAPI.update(id!, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        color: editColor,
      });
      
      setShowEditTeamModal(false);
      setActionSuccess('Team updated!');
      fetchTeam();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update team');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) return;
    
    try {
      await teamsAPI.delete(id!);
      navigate(`/organizations/${team?.workspaceId}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete team');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'LEAD': return faCrown;
      case 'ADMIN': return faUserShield;
      case 'MEMBER': return faUsers;
      default: return faUsers;
    }
  };

  const canManageTeam = hasPermission(Permission.TEAM_MANAGE_MEMBERS) || workspaceRole === 'OWNER' || workspaceRole === 'ORG_ADMIN' || workspaceRole === 'MANAGER';
  const canManageMembers = canManageTeam;

  if (loading) {
    return (
      <div className="team-detail">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading team...</p>
        </div>
      </div>
    );
  }

  if (error && !team) {
    return (
      <div className="team-detail">
        <div className="error-state">
          <FontAwesomeIcon icon={faTimes} />
          <p>{error}</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="team-detail">
      {/* Header */}
      <div className="team-header">
        <button 
          className="back-btn" 
          onClick={() => navigate(team?.workspaceId ? `/organizations/${team.workspaceId}` : '/workspaces')}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
        
        <div className="team-info">
          <div 
            className="team-icon" 
            style={{ backgroundColor: team?.color || TEAM_COLORS[0] }}
          >
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="team-meta">
            <div className="team-breadcrumb">
              {team?.workspace && (
                <Link to={`/organizations/${team.workspaceId}`} className="org-link">
                  <FontAwesomeIcon icon={faBuilding} /> {team.workspace.name}
                </Link>
              )}
            </div>
            <h1>{team?.name}</h1>
            {team?.description && (
              <p className="team-description">{team.description}</p>
            )}
            <div className="team-stats">
              <span><FontAwesomeIcon icon={faUsers} /> {members.length} members</span>
            </div>
          </div>
        </div>
        
        <div className="team-actions">
          {canManageMembers && (
            <button className="btn-secondary" onClick={() => setShowAddMemberModal(true)}>
              <FontAwesomeIcon icon={faUserPlus} /> Add Member
            </button>
          )}
          {canManageTeam && (
            <>
              <button className="btn-secondary" onClick={() => navigate(`/teams/${id}/settings`)}>
                <FontAwesomeIcon icon={faCog} /> Settings
              </button>
              <button className="btn-secondary" onClick={() => setShowEditTeamModal(true)}>
                <FontAwesomeIcon icon={faEdit} /> Edit Team
              </button>
              <button className="btn-danger" onClick={handleDeleteTeam}>
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faTimes} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      
      {actionSuccess && (
        <div className="alert alert-success">
          <FontAwesomeIcon icon={faCheck} />
          <span>{actionSuccess}</span>
        </div>
      )}

      <div className="team-content">
        {/* Members Section */}
        <section className="team-section">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faUsers} /> Team Members</h2>
          </div>
          
          {members.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faUsers} />
              <h3>No members yet</h3>
              <p>Add members to this team to start collaborating</p>
              {canManageMembers && (
                <button className="btn-primary" onClick={() => setShowAddMemberModal(true)}>
                  <FontAwesomeIcon icon={faUserPlus} /> Add First Member
                </button>
              )}
            </div>
          ) : (
            <div className="members-list">
              {members.map((member) => (
                <div key={member.id} className="member-row">
                  <div className="member-avatar">
                    {member.user.name?.charAt(0).toUpperCase() || member.user.email.charAt(0).toUpperCase()}
                  </div>
                  <div className="member-info">
                    <div className="member-name">
                      {member.user.name || member.user.email}
                      {member.role === 'LEAD' && (
                        <span className="lead-badge">
                          <FontAwesomeIcon icon={faCrown} /> Lead
                        </span>
                      )}
                    </div>
                    <div className="member-email">{member.user.email}</div>
                  </div>
                  <div className="member-role">
                    <FontAwesomeIcon icon={getRoleIcon(member.role)} />
                    {member.role}
                  </div>
                  {canManageMembers && member.role !== 'LEAD' && (
                    <div className="member-actions">
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleRemoveMember(member.user.id)}
                        title="Remove from team"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Quick Actions Section */}
        <section className="team-section quick-actions">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faChartLine} /> Quick Actions</h2>
          </div>
          <div className="actions-grid">
            <Link to="/library" className="action-card">
              <FontAwesomeIcon icon={faBox} />
              <span>View Items</span>
            </Link>
            <Link to="/audit" className="action-card">
              <FontAwesomeIcon icon={faHistory} />
              <span>Activity Log</span>
            </Link>
            <Link to="/settings" className="action-card">
              <FontAwesomeIcon icon={faCog} />
              <span>Settings</span>
            </Link>
          </div>
        </section>
      </div>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="modal-overlay" onClick={() => setShowAddMemberModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faUserPlus} /> Add Team Member</h2>
              <button className="close-btn" onClick={() => setShowAddMemberModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={memberEmail}
                  onChange={(e) => setMemberEmail(e.target.value)}
                  placeholder="member@example.com"
                />
                <small>User must be a member of the organization</small>
              </div>
              <div className="form-group">
                <label>Team Role</label>
                <select
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value)}
                >
                  <option value="LEAD">Team Lead</option>
                  <option value="ADMIN">Team Admin</option>
                  <option value="MEMBER">Member</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAddMemberModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleAddMember}
                disabled={!memberEmail.trim() || actionLoading}
              >
                {actionLoading ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditTeamModal && (
        <div className="modal-overlay" onClick={() => setShowEditTeamModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faEdit} /> Edit Team</h2>
              <button className="close-btn" onClick={() => setShowEditTeamModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Team Name *</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter team name"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Describe this team's purpose"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Team Color</label>
                <div className="color-picker">
                  {TEAM_COLORS.map((color) => (
                    <button
                      key={color}
                      className={`color-option ${editColor === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setEditColor(color)}
                    >
                      {editColor === color && <FontAwesomeIcon icon={faCheck} />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowEditTeamModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleUpdateTeam}
                disabled={!editName.trim() || actionLoading}
              >
                {actionLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetail;
